import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FormData, FormErrors } from 'types/login';
import authService from 'api/auth.service';
import propertyService from 'api/property.service';
import Button from 'components/ui/Button';
import InputEmail from 'components/ui/InputEmail';
import InputPassword from 'components/ui/InputPassword';
import Checkbox from 'components/ui/Checkbox';
import Icon from 'components/AppIcon';

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isSubmittingRef = useRef(false);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);

  // Load remembered credentials on component mount
  useEffect(() => {
    loadRememberedCredentials();
  }, []);

  // Function to load remembered credentials
  const loadRememberedCredentials = () => {
    try {
      const rememberedUser = localStorage.getItem('remembered_user');
      const isRemembered = localStorage.getItem('remember_me') === 'true';

      if (isRemembered && rememberedUser) {
        const { email } = JSON.parse(rememberedUser);

        setFormData(prev => ({
          ...prev,
          email: email || '',
          rememberMe: true,
        }));
      }
    } catch {
      // Clear potentially corrupted storage
      localStorage.removeItem('remembered_user');
      localStorage.removeItem('remember_me');
    }
  };

  // Function to save or clear remembered credentials
  const handleRememberMe = (shouldRemember: boolean, email: string) => {
    if (shouldRemember) {
      localStorage.setItem('remember_me', 'true');
      localStorage.setItem('remembered_user', JSON.stringify({ email }));
    } else {
      localStorage.removeItem('remember_me');
      localStorage.removeItem('remembered_user');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): FormErrors => {
    const newErrors: FormErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Prevent double submission
    if (isSubmittingRef.current) {
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    isSubmittingRef.current = true;
    setIsLoading(true);
    setErrors({});

    // Clear any old auth data before login attempt to prevent race conditions
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('organization');
    localStorage.removeItem('organization_id');
    // Clear remember me data before login - will be set again only if login succeeds
    localStorage.removeItem('remember_me');
    localStorage.removeItem('remembered_user');

    try {
      // Call the authentication service
      const response = await authService.login({
        email: formData.email,
        password: formData.password,
      });

      // Save the authentication token
      localStorage.setItem('auth_token', response.data.token);

      // Save user data
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Save organization data
      if (response.data.user.organization) {
        localStorage.setItem('organization', JSON.stringify(response.data.user.organization));
        localStorage.setItem('organization_id', response.data.user.organization.id);
      }

      // Handle remember me functionality
      handleRememberMe(formData.rememberMe, formData.email);

      // Set default property if not already set (skip for super_admin)
      if (response.data.user.role !== 'super_admin' && response.data.user.organization_id) {
        await setDefaultProperty(response.data.user.organization_id as string);
      }

      // Navigate to intended destination or dashboard
      let from = location.state?.from?.pathname;
      
      if (!from) {
        // Redirect super admin to admin dashboard by default
        if (response.data.user.role === 'super_admin') {
          from = '/admin/dashboard';
        } else {
          from = '/dashboard';
        }
      }

      navigate(from, { replace: true });
    } catch (error) {
      // Ensure all auth data is cleared on login error
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      localStorage.removeItem('organization');
      localStorage.removeItem('organization_id');
      // Also clear remember me data on failed login to prevent infinity loop
      localStorage.removeItem('remember_me');
      localStorage.removeItem('remembered_user');

      // Handle specific error cases
      const axiosError = error as {
        response?: {
          status: number;
          data?: {
            message?: string;
          };
        };
        request?: unknown;
      };
      if (axiosError.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (axiosError.response.status === 401) {
          setErrors({
            general: 'Invalid email or password. Please try again.',
          });
        } else if (axiosError.response.data?.message) {
          setErrors({
            general: axiosError.response.data.message,
          });
        } else {
          setErrors({
            general: 'An error occurred during login. Please try again later.',
          });
        }
      } else if (axiosError.request) {
        // The request was made but no response was received
        setErrors({
          general: 'No response from server. Please check your internet connection.',
        });
      } else {
        // Something happened in setting up the request that triggered an Error
        setErrors({
          general: 'An error occurred during login. Please try again later.',
        });
      }
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const setDefaultProperty = async (organizationId: string) => {
    if (localStorage.getItem('selectedPropertyId')) return;
    try {
      const response = await propertyService.getMyProperties(organizationId);
      const properties = response.data.properties;
      if (properties.length > 0) {
        localStorage.setItem('selectedPropertyId', properties[0].id);
        localStorage.setItem('selectedPropertyName', properties[0].name);
      }
    } catch {
      // Header will handle property loading as fallback
    }
  };

  const handleForgotPassword = () => {
    // TODO: Implement forgot password functionality
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-surface rounded-lg shadow-large p-8">
        {/* Logo and Welcome */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-10 h-10 flex items-center justify-center">
              <img src="/assets/images/logo.png" alt="Forecast Inn Logo" className="w-10 h-10 object-contain" />
            </div>
            <span className="text-2xl font-semibold text-primary font-heading">Forecast Inn</span>
          </div>
          <h1 className="text-2xl font-semibold text-text-primary mb-2">Welcome Back</h1>
          <p className="text-text-secondary">Sign in to access your revenue management dashboard</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* General Error */}
          {errors.general && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-center space-x-3">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" />
              <span className="text-error text-sm">{errors.general}</span>
            </div>
          )}

          {/* Email Field */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              {formData.email && formData.rememberMe && (
                <span className="text-xs text-primary flex items-center">
                  <Icon name="Save" size={12} className="mr-1" />
                  Remembered
                </span>
              )}
            </div>
            <InputEmail
              id="email"
              name="email"
              label="Email Address"
              placeholder="Enter your email address"
              value={formData.email}
              onChange={handleInputChange}
              error={errors.email}
            />
          </div>

          {/* Password Field */}
          <div className="space-y-2">
            <InputPassword
              id="password"
              name="password"
              label="Password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleInputChange}
              error={errors.password}
            />
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="flex items-center justify-between">
            <Checkbox
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleInputChange}
              label="Remember me"
            />
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-sm text-primary hover:text-primary/80 transition-colors"
            >
              Forgot password?
            </button>
          </div>

          {/* Sign In Button */}
          <Button type="submit" variant="primary" fullWidth loading={isLoading} disabled={isLoading} className="h-12">
            {isLoading ? 'Signing In...' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;
